import { AxiosRequestConfig, CancelToken } from "axios";
import api from "src/spintr/SpintrApi";

const listsBaseUrl = "/api/v1/QualityHubTaskLists";
const groupsBaseUrl = "/api/v1/QualityHubTaskListGroups";
const tasksBaseUrl = "/api/v1/QualityHubTaskListItems";
const statusBaseUrl = "/api/v1/QualityHubTaskListItemStatuses";
const resourceTagsBaseUrl = "/api/v1/ResourceTags";
const operationalTargetsBaseUrl = "/api/v1/OperationalTargets";

/**
 * The input model expected by the API for creating or editing a task list.
 */
export type TaskListModel
    = Omit<Spintr.TaskList, "id" | "createdOn" | "editedOn">
    & { };

/**
 * The input model expected by the API for creating or editing a task group.
 */
export type TaskGroupModel = Omit<Spintr.TaskListGroup,
    "id" | "tasks"
>;

/**
 * The input model expected by the API when creating or editing a task.
 */
export type TaskModel
    = Omit<Spintr.TaskListTask, "id" | "tags" | "createdOn" | "assignments">
    & {
        /**
         * The IDs of the tags associated with the task.
         */
        tagIds: number[];

        /**
         * The IDs of the assignees for the task.
         */
        assigneeIds?: number[];
    };

export type TaskActionModel
    = Omit<Spintr.TaskListTask, "id" | "tags" | "listId" | "createdOn" | "assignments">
    & {
        files?: File[];

        /**
         * The IDs of the assignees for the task.
         */
        assigneeIds?: number[];
    };

export type TaskStatusModel = Omit<Spintr.TaskListItemStatus,
    "id" | "reportedOn" | "reportedBy" | "uberId"
> & { files?: File[] };

export type OperationalTargetModel = Omit<Spintr.OperationalTarget, "id" | "uberId"> & {
    nodeId: number;
};

export type OperationalTargetResponse = Omit<Spintr.OperationalTarget, "startDate" | "endDate"> & {
    startDate: string;

    endDate?: string | null | undefined;
}

/**
 * The input model expected by the API when creating or editing a resource tag.
 */
export type ResourceTagModel = Omit<Spintr.ResourceTag, "id">;

function fixStatusDate(status: Spintr.TaskListItemStatus): Spintr.TaskListItemStatus {
    return {
        ...status,
        reportedOn: status.reportedOn ? new Date(status.reportedOn) : undefined,
    };
}

export function fixTaskListItemDates(taskListItem: Spintr.TaskListTask): Spintr.TaskListTask {
    return {
        ...taskListItem,
        createdOn: taskListItem.createdOn ? new Date(taskListItem.createdOn) : undefined,
        editedOn: taskListItem.editedOn ? new Date(taskListItem.editedOn) : undefined,
        dueByDate: taskListItem.dueByDate ? new Date(taskListItem.dueByDate) : undefined,
    };
}

function fixTaskListDates(taskList: Spintr.TaskList): Spintr.TaskList {
    return {
        ...taskList,
        createdOn:  new Date(taskList.createdOn),
        startDate:  new Date(taskList.startDate),
        endDate:    taskList.endDate ? new Date(taskList.endDate) : undefined,
        statuses:   taskList.statuses?.map(fixStatusDate),
        tasks:      taskList.tasks?.map(fixTaskListItemDates),
    };
}

export async function queryTaskListsAsync(
    cancelToken?: CancelToken,
): Promise<Spintr.QueryEnvelope<Spintr.TaskList>> {
    const response = await api.get<Spintr.QueryEnvelope<Spintr.TaskList>>(
        listsBaseUrl, {
        cancelToken,
    });

    return {
        ...response.data,
        items: response.data.items
            .map(fixTaskListDates)
            .filter((list) => list.listType !== 3 && list.listTarget !== 1),
    };
}

export async function findTaskListAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskList> {
    let taskList: Spintr.TaskList;

    const response = await api.get<Spintr.TaskList>(
        `${listsBaseUrl}/${id}`, {
        cancelToken,
    });

    taskList = response.data;

    return fixTaskListDates(taskList);
}

export async function createTaskListAsync(
    model: TaskListModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskList> {
    let taskList: Spintr.TaskList;

    const response = await api.post<Spintr.TaskList>(listsBaseUrl, model, {
        cancelToken,
    });

    taskList = response.data;

    return fixTaskListDates(taskList);
}

export async function updateTaskListAsync(
    id: number,
    model: TaskListModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskList> {
    let taskList: Spintr.TaskList;

    const response = await api.put<Spintr.TaskList>(
        `${listsBaseUrl}/${id}`, model, {
        cancelToken,
    });

    taskList = response.data;

    return fixTaskListDates(taskList);
}

export function deleteTaskListAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<void> {
    return new Promise<void>((resolve, _) => {
        // Mock API call until I implement the actual API
        setTimeout(() => resolve(), 300);
    });
}

/**
 * The parameters for querying the user's task lists.
 */
export type QueryMyTaskListsParams = {
    /**
     * The ID of the node to filter task lists by.
     */
    nodeId?: number | undefined;
    
    /**
     * The date to filter task lists as of.
     */
    asOf?: Date | undefined;
};

/**
 * Queries the API for the user's task lists.
 *
 * @param {QueryMyTaskListsParams} [params={}] - The query parameters.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskList[]>} - A promise that resolves to the task lists.
 */
export async function queryMyTaskListsAsync(
    params: QueryMyTaskListsParams = {},
    cancelToken?: CancelToken,
): Promise<Spintr.TaskList[]> {
    const response = await api.get<Spintr.QueryEnvelope<Spintr.TaskList>>(
        `${listsBaseUrl}/my`,
        {
            cancelToken,
            params,
        });

    return response.data.items.map(fixTaskListDates);
}

/**
 * Creates a task list item asynchronously.
 *
 * @param {TaskModel} model - The task model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 */
export async function createTaskListItemAsync(
    model: TaskModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListTask> {
    const formData = modelToFormData(model);
    
    const response = await api.post<Spintr.TaskListTask>(
        tasksBaseUrl,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return fixTaskListItemDates(response.data);
}

function writeFormDataPair(key: string, value: any, fd: FormData): void {
    if (value === null || value === undefined) {
        return;
    }

    if (value instanceof File) {
        console.log(key);
        fd.append(key, value, value.name);
        return;
    }

    if (value instanceof Date) {
        fd.append(key, value.toISOString());
        return;
    }

    if (Array.isArray(value)) {
        value.forEach((v) => writeFormDataPair(key, v, fd));
        return;
    }

    console.log(`Coding key ${key} as string:`, value);
    return fd.append(key, value.toString());
}

function modelToFormData<TModel = any>(model: TModel): FormData {
    const formData = new FormData();

    Object
        .entries(model)
        .forEach(([key, value]) => writeFormDataPair(key, value, formData));

    return formData;
}

/**
 * Updates a task list item asynchronously.
 *
 * @param {number} id - The ID of the task list item to be updated.
 * @param {TaskModel} model - The task model with updated data.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListTask>} - The updated task list item.
 */
export async function updateTaskListItemAsync(
    id: number,
    model: TaskModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListTask> {
    const formData = modelToFormData(model);

    const response = await api.put<Spintr.TaskListTask>(
        `${tasksBaseUrl}/${id}`,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return fixTaskListItemDates(response.data);
}

export async function setTaskListGroupOverridesAsync(
    listId: number,
    groupOverrides: Record<string, Partial<Spintr.TaskListGroup>>,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.put<void>(
        `${listsBaseUrl}/${listId}/overrides/groups`, groupOverrides, {
        cancelToken,
    });
}

/**
 * Updates multiple task list item overrides asynchronously.
 *
 * @param {number} listId - The ID of the task list.
 * @param {Record<string, Partial<Spintr.TaskListTask>>} overrides - The task overrides to apply.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<void>} - A promise that resolves when the overrides are updated.
 */
export async function updateTaskListItemOverridesAsync(
    listId: number,
    overrides: Record<string, Partial<Spintr.TaskListTask>>,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.put<void>(
        `${listsBaseUrl}/${listId}/overrides/items`, overrides, {
        cancelToken,
    });
}

/**
 * Creates a task list action asynchronously.
 *
 * @param {number} parentTask - The ID of the parent task for the action.
 * @param {TaskActionModel} model - The task action model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListTask>} - The created task list action.
 */
export async function createTaskListActionAsync(
    parentTask: number,
    model: TaskActionModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListTask> {
    const formData = modelToFormData(model);

    const response = await api.post<Spintr.TaskListTask>(
        `${tasksBaseUrl}/${parentTask}/actions`,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return fixTaskListItemDates(response.data);
}

/**
 * Create a task list group asynchronously.
 *
 * @param {TaskGroupModel} model - The task group model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListGroup>} - The created task list group.
 */
export async function createTaskListGroupAsync(
    model: TaskGroupModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListGroup> {
    const response = await api.post<Spintr.TaskListGroup>(
        groupsBaseUrl, model, {
        cancelToken,
    });

    return response.data;
}

/**
 * Updates a task list group asynchronously.
 *
 * @param {number} id - The ID of the task list group to be updated.
 * @param {TaskGroupModel} model - The task group model with updated data.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListGroup>} - The updated task list group.
 */
export async function updateTaskListGroupAsync(
    id: number,
    model: TaskGroupModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListGroup> {
    const response = await api.put<Spintr.TaskListGroup>(
        `${groupsBaseUrl}/${id}`, model, {
        cancelToken,
    });

    return response.data;
}

/**
 * Creates a task list item status asynchronously.
 *
 * @param {TaskStatusModel} model - The task status model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListItemStatus>} - The created task list item status.
 */
export async function createTaskListItemStatusAsync(
    model: TaskStatusModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListItemStatus> {
    const formData = new FormData();
    formData.append("itemId", model.itemId.toString());
    formData.append("status", model.status.toString());

    if (model.files) {
        model.files.forEach((file) => {
            formData.append("files", file, file.name);
        });
    }

    const response = await api.post<Spintr.TaskListItemStatus>(
        statusBaseUrl,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return response.data;
}

/**
 * Updates a task list item status asynchronously.
 *
 * @param {number} id - The ID of the task list item status to be updated.
 * @param {TaskStatusModel} model - The task status model with updated data.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.TaskListItemStatus>} - The updated task list item status.
 */
export async function updateTaskListItemStatusAsync(
    id: number,
    model: TaskStatusModel,
    cancelToken?: CancelToken,
): Promise<Spintr.TaskListItemStatus> {
    const formData = new FormData();
    formData.append("itemId", model.itemId.toString());
    formData.append("status", model.status.toString());

    if (model.files) {
        model.files.forEach((file) => {
            formData.append("files", file, file.name);
        });
    }

    const response = await api.put<Spintr.TaskListItemStatus>(
        `${statusBaseUrl}/${id}`,
        formData,
        {
            cancelToken,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

    return response.data;
}

/**
 * Deletes a task list item status asynchronously.
 *
 * @param {number} id - The ID of the task list item status to be deleted.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<void>} - A promise that resolves when the deletion is complete.
 */
export async function deleteTaskListItemStatusAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.delete<void>(
        `${statusBaseUrl}/${id}`, {
        cancelToken,
    });
}

/**
 * Queries the API for resource tags.
 *
 * @param {number} [skip=0] - The number of items to skip.
 * @param {number} [take=100] - The number of items to take.
 * @param {string} [search] - The search term to filter the tags.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.QueryEnvelope<Spintr.ResourceTag>>} - A promise that resolves to the resource tags.
 */
export async function queryResourceTagsAsync(
    skip: number = 0,
    take: number = 100,
    search?: string,
    cancelToken?: CancelToken,
): Promise<Spintr.QueryEnvelope<Spintr.ResourceTag>> {
    const params: AxiosRequestConfig = {
        params: {
            skip,
            take,
            search,
        },
        cancelToken,
    };

    const response = await api.get<Spintr.QueryEnvelope<Spintr.ResourceTag>>(
        resourceTagsBaseUrl, params,
    );

    return response.data;
}

/**
 * Finds a resource tag by ID asynchronously.
 *
 * @param {number} id - The ID of the resource tag to be found.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.ResourceTag>} - The found resource tag.
 * */
export async function findResourceTagAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<Spintr.ResourceTag> {
    const response = await api.get<Spintr.ResourceTag>(
        `${resourceTagsBaseUrl}/${id}`, {
        cancelToken,
    });

    return response.data;
}

/**
 * Creates a resource tag asynchronously.
 *
 * @param {ResourceTagModel} model - The resource tag model to be created.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.ResourceTag>} - The created resource tag.
 */
export async function createResourceTagAsync(
    model: ResourceTagModel,
    cancelToken?: CancelToken,
): Promise<Spintr.ResourceTag> {
    const response = await api.post<Spintr.ResourceTag>(
        resourceTagsBaseUrl, model, {
        cancelToken,
    });

    return response.data;
}

/**
 * Updates a resource tag asynchronously.
 *
 * @param {number} id - The ID of the resource tag to be updated.
 * @param {ResourceTagModel} model - The resource tag model with updated data.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<Spintr.ResourceTag>} - The updated resource tag.
 */
export async function updateResourceTagAsync(
    id: number,
    model: ResourceTagModel,
    cancelToken?: CancelToken,
): Promise<Spintr.ResourceTag> {
    const response = await api.put<Spintr.ResourceTag>(
        `${resourceTagsBaseUrl}/${id}`, model, {
        cancelToken,
    });

    return response.data;
}

/**
 * Deletes a resource tag asynchronously.
 *
 * @param {number} id - The ID of the resource tag to be deleted.
 * @param {CancelToken} [cancelToken] - The cancel token to cancel the request.
 * @returns {Promise<void>} - A promise that resolves when the deletion is complete.
 */
export async function deleteResourceTagAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.delete<void>(
        `${resourceTagsBaseUrl}/${id}`, {
        cancelToken,
    });
}

function fixOperationalTargetDates(opsTarget: OperationalTargetResponse): Spintr.OperationalTarget {
    return {
        ...opsTarget,
        startDate: new Date(opsTarget.startDate),
        endDate: opsTarget.endDate ? new Date(opsTarget.endDate) : undefined,
    };
}

export async function queryOperationalTargetsAsync(
    cancelToken?: CancelToken,
): Promise<Spintr.QueryEnvelope<Spintr.OperationalTarget>> {
    const response = await api.get<Spintr.QueryEnvelope<OperationalTargetResponse>>(
        operationalTargetsBaseUrl,
        {
            cancelToken,
        });

    return {
        ...response.data,
        items: response.data.items.map(fixOperationalTargetDates),
    };
}

export async function findOperationalTargetAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<Spintr.OperationalTarget> {
    const response = await api.get<OperationalTargetResponse>(
        `${operationalTargetsBaseUrl}/${id}`, {
        cancelToken,
    });

    return fixOperationalTargetDates(response.data);
}

export async function createOperationalTargetAsync(
    model: OperationalTargetModel,
    cancelToken?: CancelToken,
): Promise<Spintr.OperationalTarget> {
    const response = await api.post<OperationalTargetResponse>(
        operationalTargetsBaseUrl, model, {
        cancelToken,
    });

    return fixOperationalTargetDates(response.data);
}

export async function updateOperationalTargetAsync(
    id: number,
    model: OperationalTargetModel,
    cancelToken?: CancelToken,
): Promise<Spintr.OperationalTarget> {
    const response = await api.put<OperationalTargetResponse>(
        `${operationalTargetsBaseUrl}/${id}`, model, {
        cancelToken,
    });

    return fixOperationalTargetDates(response.data);
}

export async function deleteOperationalTargetAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.delete<void>(
        `${operationalTargetsBaseUrl}/${id}`, {
        cancelToken,
    });
}

type QualityHubReportDto = Omit<Spintr.QualityHubReport, "date"> & {
    date: string;
};

type QualityHubReportsParams = {
    startDate: Date;
    endDate: Date;
    nodeId?: number;
    listId?: number;
};

export async function getQualityHubReportsSummary(
    params?: QualityHubReportsParams,
    cancelToken?: CancelToken,
): Promise<Spintr.QualityHubReport[]> {
    const response = await api.get<QualityHubReportDto[]>(
        "/api/v1/QualityHubReports/summary",
        { 
            params,
            cancelToken,
        });

    return response.data.map((report) => ({
        ...report,
        date: new Date(report.date),
    }));
}

type QualityHubUnitBreakdownParams = {
    startDate: Date;
    endDate: Date;
    nodeId?: number;
    listId?: number;
    page?: number;
    pageSize?: number;
};

export async function getQualityHubUnitBreakdownReportAsync(
    params?: QualityHubUnitBreakdownParams,
    cancelToken?: CancelToken,
): Promise<Spintr.QualityHubUnitBreakdownReport> {
    const response = await api.get<Spintr.QualityHubUnitBreakdownReport>(
        "/api/v1/QualityHubReports/breakdown/units",
        { 
            params,
            cancelToken,
        });

    return response.data;
}

export async function getQualityHubListBreakdownReportAsync(
    params?: QualityHubUnitBreakdownParams,
    cancelToken?: CancelToken,
): Promise<Spintr.QualityHubUnitBreakdownReport> {
    const response = await api.get<Spintr.QualityHubUnitBreakdownReport>(
        "/api/v1/QualityHubReports/breakdown/lists",
        { 
            params,
            cancelToken,
        });

    return response.data;
}
    
